import React from "react";
import ReactDOM from "react-dom/client";
import { createBrowserRouter, Outlet, RouterProvider, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet";
import ReactGA from "react-ga4";
import "./index.css";
import "@styles/colours.module.css";
import ErrorPage from "@pages/Error/ErrorPage";
import AppLayout from "@components/layout/AppLayout/AppLayout";
import authRoutes from "@routes/authRoutes";
import charitiesRoutes from "@routes/charitiesRoutes";
import businessesRoutes from "@routes/businessesRoutes";
import directorsRoutes from "@routes/directorsRoutes";
import upcomingServiceRoutes from "@routes/upcomingServiceRoutes";
import supportRoutes from "@routes/supportRoutes";
import memorialsRoutes from "@routes/memorialsRoutes";
import accountRoutes from "@routes/accountRoutes";
import AuthLayout from "@components/layout/AuthLayout/AuthLayout";
import StyleGuidePage from "@pages/Dev/StyleGuidePage";
import HomePage from "@pages/Home/HomePage";
import { AuthProvider } from "@context/auth-context";
import { BrandingOverlayProvider } from "@context/branding-overlay-context";
import MaintenancePage from "@pages/Error/MaintenancePage";
import UnauthorisedPage from "@pages/Error/UnauthorisedPage";
import anniversariesRoutes from "@routes/anniversariesRoutes";
import usePageTracking from "@hooks/use-page-tracking";
import notificationsRoutes from "@routes/notificationsRoutes";
import ManageTributePage from "@pages/Memorials/ManageTributePage";

const isLocalhost = window.location.hostname === "localhost"
    || window.location.hostname.endsWith(".localhost");

if (!isLocalhost && !/^\/tribute\/manage\/?$/i.test(window.location.pathname)) {
    ReactGA.initialize("G-NF2673GH79");
}

if (process.env.NODE_ENV === "development" || process.env.NODE_ENV === "test") {
    console.log(`App running in ${process.env.NODE_ENV} mode.`);
}

const root = ReactDOM.createRoot(
    document.getElementById("root") as HTMLElement,
);

function AnalyticsTracker() {
    usePageTracking();
    return null; // doesn’t render anything, just runs the hook
}

// Self-referencing canonical (current host and path, no query), so it is correct on every hub domain
function CanonicalLink() {
    const { pathname } = useLocation();
    return (
        <Helmet>
            <link rel="canonical" href={`${window.location.origin}${pathname}`} />
        </Helmet>
    );
}

const router = createBrowserRouter([
    {
        path: "/",
        element: (
            <>
                <AnalyticsTracker />
                <CanonicalLink />
                <Outlet />
            </>
        ),
        errorElement: <ErrorPage />,
        children: [
            {
                path: "",
                element: <AuthLayout />,
                children: [
                    {
                        path: "",
                        element: <HomePage />,
                    },
                ],
            },
            {
                element: <AuthLayout />,
                children: [...authRoutes, { path: "tribute/manage", element: <ManageTributePage /> }],
            },
            {
                element: <AppLayout />,
                children: [
                    ...charitiesRoutes,
                    ...businessesRoutes,
                    ...directorsRoutes,
                    ...anniversariesRoutes,
                    ...upcomingServiceRoutes,
                    ...memorialsRoutes,
                    ...accountRoutes,
                    ...notificationsRoutes,
                    ...supportRoutes,
                ],
            },
            {
                path: "/maintenance",
                element: <MaintenancePage />,
            },
            {
                path: "/unauthorised",
                element: <UnauthorisedPage />,
            },
            ...(process.env.NODE_ENV === "development" ? [
                {
                    path: "/style-guide",
                    element: (
                        <React.Suspense fallback={<div>Loading Style Guide...</div>}>
                            <StyleGuidePage />
                        </React.Suspense>
                    ),
                },
            ] : []),
        ],
    },
]);

root.render(
    <React.StrictMode>
        <AuthProvider>
            <BrandingOverlayProvider>
                <RouterProvider router={router} />
            </BrandingOverlayProvider>
        </AuthProvider>
    </React.StrictMode>
);
